import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, CircularProgress, Alert,
    Accordion, AccordionSummary, AccordionDetails,
    List, ListItemButton, ListItemText, ListItemIcon,
    Chip,
} from '@mui/material';
import {
    ExpandMore, ElectricCar, LocalShipping, Folder,
} from '@mui/icons-material';
import { useAuth, API_BASE_URL } from '@vortx/shared-ui';

export default function ImportFluxEVDialog({ open, onClose, onImport }) {
    const { currentUser } = useAuth();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !currentUser) return;

        const fetchProjects = async () => {
            setError(null);
            setLoading(true);
            try {
                const token = await currentUser.getIdToken();
                const response = await fetch(`${API_BASE_URL}/api/site_dna/v1/load-profile/flux-ev-projects`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch Flux EV projects');
                }

                const data = await response.json();
                setProjects(data.projects || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, [open, currentUser]);

    const handleSelectModel = async (project, modelType) => {
        try {
            setImporting(true);
            setError(null);

            const token = await currentUser.getIdToken();
            const response = await fetch(
                `${API_BASE_URL}/api/site_dna/v1/load-profile/flux-ev-load/${project.id}?model_type=${modelType}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch load profile');
            }

            const data = await response.json();

            onImport(data.loads_kw, {
                source: 'flux_ev',
                projectId: project.id,
                projectName: project.name,
                projectType: project.project_type,
                modelType: modelType,
                peakKw: data.peak_kw,
                annualKwh: data.annual_kwh,
            });
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setImporting(false);
        }
    };

    const getModelIcon = (modelType) => {
        if (modelType === 'fleet') return <ElectricCar />;
        if (modelType === 'depot') return <LocalShipping />;
        return <Folder />;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Import from Flux EV</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select a project and model type to import its 8760 hourly load profile.
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : projects.length === 0 ? (
                    <Alert severity="info">
                        No Flux EV projects with load data found.
                        Create a fleet simulation or depot load in Flux EV first.
                    </Alert>
                ) : (
                    projects.map(project => (
                        <Accordion key={project.id} defaultExpanded={projects.length === 1}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Folder sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography fontWeight={600}>{project.name}</Typography>
                                <Chip
                                    label={project.project_type}
                                    size="small"
                                    sx={{ ml: 1, textTransform: 'capitalize' }}
                                />
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <List dense disablePadding>
                                    {project.available_models.map(model => (
                                        <ListItemButton
                                            key={model.type}
                                            onClick={() => handleSelectModel(project, model.type)}
                                            disabled={importing}
                                            sx={{ borderRadius: 1 }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                {getModelIcon(model.type)}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={model.label}
                                                secondary={[
                                                    model.peak_kw != null && `Peak: ${Math.round(model.peak_kw)} kW`,
                                                    model.annual_kwh != null && `Annual: ${Math.round(model.annual_kwh).toLocaleString()} kWh`,
                                                    `${model.intervals} intervals`,
                                                ].filter(Boolean).join(' · ')}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    ))
                )}
                {importing && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Importing load profile...</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={importing}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}
